import { HttpErrorResponse } from '@angular/common/http';
import {
  DestroyRef,
  inject,
  Renderer2,
  RendererFactory2,
  Service,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';

import { HttpStatusCode, UserSettings } from '@models';
import {
  AppEvent,
  AppEventType,
  AuthenticationService,
  EventQueueService,
  ModalService,
  RoutesService,
  SamlService,
  UtilitiesService,
} from '@services';
import { logError, matchAbove } from '@shared/utils';
import {
  AppState,
  resetCurrentResource,
  setIsLoggedIn,
  setNavigationOpen,
  setRequestedUrl,
} from '@store';
import { ModalResourceHandlerService } from './modal-resource-handler.service';

const isSignedOutResponse = (error: unknown) =>
  error instanceof HttpErrorResponse &&
  error.status === HttpStatusCode.UNAUTHORIZED;

@Service()
export class AppInitService {
  destroyRef = inject(DestroyRef);
  router = inject(Router);
  store = inject<Store<AppState>>(Store);
  routesService = inject(RoutesService);
  modalService = inject(ModalService);
  modalResourceHandlerService = inject(ModalResourceHandlerService);
  authenticationService = inject(AuthenticationService);
  samlService = inject(SamlService);
  utilitiesService = inject(UtilitiesService);
  eventQueueService = inject(EventQueueService);
  rendererFactory = inject(RendererFactory2);

  userBean!: UserSettings;
  linkClickUnlisten: (() => void) | undefined;
  isLoggedIn!: boolean;
  lastRedirectUrl = '';
  renderer: Renderer2;

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.store
      .select((state) => state)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.userBean = state.user;
        this.isLoggedIn = state.auth.isLoggedIn;
      });

    this.destroyRef.onDestroy(() => {
      this.linkClickUnlisten?.();
    });
  }

  initializeApp = async () => {
    this.detectTouchDevice();
    this.initNavigationResize();
    this.modalResourceHandlerService.initialize();

    try {
      await this.samlService.getSamlBaseURL();

      await this.authenticationService.getUserData();
    } catch (error) {
      if (!isSignedOutResponse(error)) {
        logError(error);
      }
    }

    this.store.dispatch(
      setIsLoggedIn({
        payload: this.authenticationService.isLoggedIn(),
      })
    );

    const openedUrl = window.location.pathname + window.location.search;
    const isLinkForAnotherSession = this.isLinkForAnotherSession(openedUrl);

    if (isLinkForAnotherSession) {
      this.openDifferentSessionDialog(openedUrl);
    }

    this.linkClickUnlisten = this.renderer.listen(
      'document',
      'click',
      (event: Event) => {
        const mouseEvent = event as MouseEvent;

        if (
          mouseEvent.metaKey ||
          mouseEvent.ctrlKey ||
          mouseEvent.shiftKey ||
          mouseEvent.altKey
        ) {
          return;
        }

        const target = event.target;

        if (!(target instanceof HTMLElement)) {
          return;
        }

        const link = target.closest('a');

        if (link && this.leavesPageInThisTab(link)) {
          this.modalService.closeAll();
        }
      }
    );

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationError
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.handleNavigationEvent(event));

    const currentPath = this.router.url;
    const initialRoute = this.routesService.previousRoutePath() || '/home';

    if (
      !isLinkForAnotherSession &&
      initialRoute &&
      initialRoute !== this.lastRedirectUrl &&
      !this.routesService.getIsPublicPath(currentPath)
    ) {
      this.router.navigateByUrl(initialRoute, { replaceUrl: true });
    }

    this.handleUserSession();
  };

  leavesPageInThisTab = (link: HTMLAnchorElement) => {
    const href = link.getAttribute('href');

    if (
      !href ||
      href.startsWith('#') ||
      link.hasAttribute('download') ||
      (link.target !== '' && link.target !== '_self')
    ) {
      return false;
    }

    return (
      URL.parse(href, window.location.origin)?.origin === window.location.origin
    );
  };

  saveLastUrl = (path: Nullable<string> = null) => {
    const publicPaths = [
      '',
      '/',
      'login',
      'finma-communities',
      'finma-registration',
      'registration',
      'activation',
      'cec',
      '503',
      '404',
      'price-calculator',
    ];

    const isPublicPath = !!path && publicPaths.indexOf(path) > -1;

    if (!isPublicPath && path) {
      this.routesService.setPreviousPath(path);
    }
  };

  handleNavigationEvent = async (
    event: NavigationStart | NavigationEnd | NavigationError
  ) => {
    if (event.url === this.lastRedirectUrl) {
      return;
    }

    if (
      (this.routesService.previousRoutePath().startsWith('/rooms') ||
        this.routesService.previousRoutePath().startsWith('/shared-with-me') ||
        this.routesService.previousRoutePath().startsWith('/events')) &&
      !event.url.startsWith('/search') &&
      !event.url.startsWith('/rooms') &&
      !event.url.startsWith('/events') &&
      !event.url.startsWith('/shared-with-me')
    ) {
      this.store.dispatch(resetCurrentResource());
    }

    if (this.getBasePath(event.url) === 'login') {
      return;
    }

    if (this.isLinkForAnotherSession(event.url)) {
      this.openDifferentSessionDialog(event.url);

      return;
    }

    if (event instanceof NavigationEnd) {
      this.dropSignInParamsOfThisSession(event.url);
    }

    if (
      event instanceof NavigationError &&
      event.error?.status === HttpStatusCode.UNAUTHORIZED
    ) {
      this.saveLastUrl(this.router.url);
      this.utilitiesService.redirectPage('/login');
      this.lastRedirectUrl = '/login';
    }
  };

  getBasePath = (url: string) =>
    this.router.parseUrl(url).root.children['primary']?.segments[0]?.path || '';

  getLinkSignInParams = (url: string) => {
    const { community, username, authKey } =
      this.router.parseUrl(url).queryParams;

    return this.getBasePath(url) === 'login' || !this.isLoggedIn
      ? null
      : { community, username, authKey };
  };

  isLinkForAnotherSession = (url: string) => {
    const { community, username } = this.getLinkSignInParams(url) ?? {};

    return (
      (!!community && community !== this.userBean.communityLoginName) ||
      (!!username && username !== this.userBean.username)
    );
  };

  openDifferentSessionDialog = (url: string) => {
    this.store.dispatch(
      setRequestedUrl({
        payload: window.location.origin + url,
      })
    );
    this.eventQueueService.dispatch(
      new AppEvent(AppEventType.OPEN_DIFFERENT_SESSION_DIALOG)
    );
    this.router.navigate(['/home']);
    this.lastRedirectUrl = '/home';
  };

  dropSignInParamsOfThisSession = (url: string) => {
    const { community, username, authKey } =
      this.getLinkSignInParams(url) ?? {};

    if (
      (community || username || authKey) &&
      !this.router.currentNavigation()
    ) {
      this.router.navigate([], {
        queryParams: { community: null, username: null, authKey: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  };

  handleUserSession = () => {
    if (this.userBean.samlUser && !this.isLoggedIn) {
      this.utilitiesService.redirectPage('/finma-communities');
      this.lastRedirectUrl = '/finma-communities';
    } else if (this.userBean.mustAcceptConditions) {
      this.utilitiesService.redirectPage('/login');
      this.lastRedirectUrl = '/login';
    }
  };

  detectTouchDevice = () => {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (isTouch) {
      document.body.classList.add('scroll-visible');
    }
  };

  initNavigationResize = () => {
    const mql = matchAbove('md');

    mql.addEventListener('change', (e: MediaQueryListEvent) => {
      if (e.matches) {
        this.store.dispatch(setNavigationOpen({ payload: false }));
      }
    });
  };
}
